import {
  type StudyRunCaseDashboardPerformanceMetricDto,
  StudyRunCaseDashboardPerformanceMetricDtoKindEnum,
  type StudyRunCaseDashboardPerformanceStageDto,
} from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import { type FC } from 'react';
import {
  InfoTooltip,
  TruncatedMetricText,
  formatNullableScore,
  metricScoreClass,
} from './meia-p4-dashboard-primitives.component';
import { scoreFromBand } from './meia-p4-run-cases.util';

type PerformancePanelProps = {
  stages: StudyRunCaseDashboardPerformanceStageDto[];
};

export const PerformancePanel: FC<PerformancePanelProps> = (props) => {
  const { stages } = props;

  return (
    <Tile
      contentClassName="gap-3"
      header={{
        actions: (
          <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400">Current run output</span>
        ),
        title: (
          <span className="inline-flex items-center gap-1.5">
            MoP · MoE Performance
            <InfoTooltip>
              MoE rows show scored effectiveness measures. MoP rows show observed performance values when available,
              otherwise the real score used by the hierarchy.
            </InfoTooltip>
          </span>
        ),
      }}
    >
      <div className="flex max-h-[420px] flex-col overflow-y-auto rounded-xl border border-gray-200">
        {stages.length === 0 ? (
          <div className="p-3 text-xs text-gray-500">No measure data available for this thread.</div>
        ) : (
          stages.map((stage, index) => (
            <PerformanceStage
              index={index}
              key={stage.stageId}
              stage={stage}
            />
          ))
        )}
      </div>
    </Tile>
  );
};

type PerformanceStageProps = {
  index: number;
  stage: StudyRunCaseDashboardPerformanceStageDto;
};

const PerformanceStage: FC<PerformanceStageProps> = (props) => {
  const { index, stage } = props;

  return (
    <section
      className={cn(
        'grid grid-cols-[112px_minmax(0,1fr)] gap-4 border-b border-gray-200 p-3 last:border-b-0',
        index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70',
      )}
    >
      <div>
        <div className="text-sm font-semibold text-gray-950">{stage.stageName}</div>
        <div className="mt-1 font-mono text-[10px] text-gray-400">
          {stage.metrics.length} metric{stage.metrics.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="flex min-w-0 flex-col divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
        {stage.metrics.length === 0 ? (
          <div className="px-3 py-2 text-xs text-gray-400">No linked MoE/MoP data.</div>
        ) : (
          stage.metrics.map((metric) => (
            <PerformanceMetric
              key={`${stage.stageId}-${metric.kind}-${metric.id}`}
              metric={metric}
            />
          ))
        )}
      </div>
    </section>
  );
};

type PerformanceMetricProps = {
  metric: StudyRunCaseDashboardPerformanceMetricDto;
};

const PerformanceMetric: FC<PerformanceMetricProps> = (props) => {
  const { metric } = props;
  const score = metric.score ?? scoreFromBand(metric.band);
  const detail = performanceMetricDetail(metric);
  const isObservedMetric = Boolean(metric.value);
  const value = metric.value
    ? formatObservedMetricValue({ unit: metric.unit, value: metric.value })
    : formatNullableScore(score);

  return (
    <div className="grid grid-cols-[42px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2 text-xs">
      <span
        className={cn(
          'rounded bg-gray-100 px-1.5 py-0.5 text-center font-mono uppercase',
          metric.kind === StudyRunCaseDashboardPerformanceMetricDtoKindEnum.MOE ? 'text-orange-600' : 'text-blue-700',
        )}
      >
        {metric.kind}
      </span>
      <span className="min-w-0">
        <TruncatedMetricText
          className="text-gray-800"
          text={metric.label}
        />
        {detail ? (
          <TruncatedMetricText
            className="mt-0.5 font-mono text-[10px] text-gray-400"
            text={detail}
          />
        ) : null}
      </span>
      <span className="min-w-16 text-right">
        {isObservedMetric ? (
          <>
            <span className="block font-mono text-gray-700">{value}</span>
            <span className="mt-0.5 block font-mono text-[10px] uppercase text-gray-400">observed</span>
          </>
        ) : (
          <span className={cn('block font-mono', metricScoreClass(metric.band))}>
            {value === '-' ? (
              value
            ) : (
              <>
                {value}/100 <span className="text-[10px] text-gray-400">score</span>
              </>
            )}
          </span>
        )}
      </span>
    </div>
  );
};

const formatObservedMetricValue = (params: { unit?: string | null; value: string }): string => {
  const { unit, value } = params;
  const numericValue = Number(value);
  const formattedValue = Number.isFinite(numericValue)
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(numericValue)
    : value;
  return unit ? `${formattedValue} ${unit}` : formattedValue;
};

const performanceMetricDetail = (metric: StudyRunCaseDashboardPerformanceMetricDto): string | null => {
  if (metric.kind === StudyRunCaseDashboardPerformanceMetricDtoKindEnum.MOE) {
    return null;
  }

  const details = [
    metric.threshold ? `Threshold ${metric.threshold}` : null,
    metric.objective ? `Objective ${metric.objective}` : null,
  ].filter((detail): detail is string => Boolean(detail));

  if (details.length === 0) return null;
  return details.join(' · ');
};
